import React, { useEffect, useRef, useState } from 'react'

/**
 * Canvas를 사용한 자막 미리보기 컴포넌트 - v2 (무한 렌더링 수정)
 * 이미지와 비디오 모두 지원
 */
function CanvasSubtitlePreview({ 
  mediaFile,     // 이미지 또는 비디오 파일
  mediaType,     // 'image' 또는 'video'
  mp4Url,        // MP4 URL (있을 경우)
  text,          // 자막 텍스트
  settings,      // 자막 설정
  videoSettings, // 비디오 위치 설정
  titleText,     // 제목 텍스트
  titleSettings, // 제목 설정
  width = 360,   // Canvas 너비
  height = 640   // Canvas 높이 (9:16 비율)
}) {
  const canvasRef = useRef(null)
  const videoRef = useRef(null)
  const imageRef = useRef(new Image())
  const animationRef = useRef(null)
  const [imageLoaded, setImageLoaded] = useState(false) // 이미지 로드 상태
  const [videoReady, setVideoReady] = useState(false) // 비디오 메타데이터 로드 상태

  // 제목 그리기 함수 (useCallback 제거로 의존성 문제 해결)
  const drawTitle = (ctx, canvasWidth, canvasHeight) => {
    // titleText가 없으면 titleSettings.text 사용
    const textToRender = titleText || titleSettings?.text || ''
    
    // 제목이 없거나 비활성화되어 있으면 그리지 않음
    if (!textToRender || !titleSettings?.enabled) return

    const {
      fontSize = 65,
      yPosition = 100,
      fontColor = '#FFFFFF',
      fontWeight = 'heavy',
      backgroundEnabled = true,
      backgroundColor = '#000000',
      backgroundOpacity = 0.8,
      backgroundPadding = 15,
      displayDuration = 'full'
    } = titleSettings || {}

    // 표시 시간 체크 (비디오인 경우) - 현재는 사용 안함
    // TODO: 비디오 재생 시간 추적 기능 추가 필요
    if (displayDuration !== 'full' && mp4Url) {
      // 현재는 항상 표시
    }

    // 크기 비율 계산 (1080x1920 기준으로 스케일)
    const scaleX = canvasWidth / 1080
    const scaleY = canvasHeight / 1920
    const scale = Math.min(scaleX, scaleY)

    // 스케일된 값들
    const scaledFontSize = Math.round(fontSize * scale)
    const scaledYPosition = yPosition * scaleY
    const scaledPadding = backgroundPadding * scale

    // 폰트 굵기 매핑
    const fontWeightMap = {
      'heavy': '900',
      'extrabold': '800',
      'bold': '700'
    }
    const actualFontWeight = fontWeightMap[fontWeight] || '900'

    // 폰트 설정
    ctx.font = `${actualFontWeight} ${scaledFontSize}px "Pretendard", "Noto Sans KR", sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top' // 제목은 상단 기준

    // 멀티라인 처리
    const lines = textToRender.split('\n')
    const lineHeight = scaledFontSize * 1.3
    let maxWidth = 0
    
    // 각 줄의 최대 너비 계산
    lines.forEach(line => {
      const metrics = ctx.measureText(line)
      maxWidth = Math.max(maxWidth, metrics.width)
    })

    // 위치 계산
    const x = canvasWidth / 2
    const y = scaledYPosition

    // 배경 그리기
    if (backgroundEnabled) {
      ctx.save()
      
      // 배경색 + 투명도
      const alpha = Math.min(Math.max(backgroundOpacity, 0), 1)
      ctx.fillStyle = backgroundColor
      ctx.globalAlpha = alpha
      
      // 멀티라인을 위한 전체 배경 박스
      const totalHeight = lines.length * lineHeight
      ctx.fillRect(
        x - maxWidth / 2 - scaledPadding,
        y - scaledPadding,
        maxWidth + scaledPadding * 2,
        totalHeight + scaledPadding * 2
      )
      
      ctx.restore()
    }

    // 텍스트 그리기 (멀티라인 지원)
    ctx.save()
    
    // 외각선 효과 (stroke) - 제목은 더 두께
    ctx.strokeStyle = '#000000'  // 검은색 외각선
    ctx.lineWidth = 5 * scale     // 제목은 더 두꺼운 외각선
    ctx.lineJoin = 'round'        // 모서리 둥글게
    ctx.miterLimit = 2
    
    // 그림자 효과 (강화)
    ctx.shadowColor = 'rgba(0, 0, 0, 0.95)'
    ctx.shadowBlur = 8 * scale
    ctx.shadowOffsetX = 4 * scale
    ctx.shadowOffsetY = 4 * scale
    
    // 먼저 외각선 그리기
    lines.forEach((line, index) => {
      const lineY = y + (index * lineHeight)
      ctx.strokeText(line, x, lineY)
    })
    
    // 그 다음 내부 텍스트 채우기
    ctx.fillStyle = fontColor
    lines.forEach((line, index) => {
      const lineY = y + (index * lineHeight)
      ctx.fillText(line, x, lineY)
    })
    
    ctx.restore()
  }

  // 자막 그리기 함수 (useCallback 제거로 의존성 문제 해결)
  const drawSubtitle = (ctx, canvasWidth, canvasHeight) => {
    if (!text) return
    
    // enabled 상태 체크 추가
    if (settings && settings.enabled === false) return

    const {
      fontSize = 50,
      yPosition = 1500,
      fontColor = '#FFFFFF',
      backgroundEnabled = true,
      backgroundColor = '#000000',
      backgroundOpacity = 0.7,
      backgroundPadding = 10,
      fontWeight = 'bold'
    } = settings || {}

    // 크기 비율 계산 (1080x1920 기준으로 스케일)
    const scaleX = canvasWidth / 1080
    const scaleY = canvasHeight / 1920
    const scale = Math.min(scaleX, scaleY)

    // 스케일된 값들
    const scaledFontSize = Math.round(fontSize * scale)
    const scaledYPosition = yPosition * scaleY
    const scaledPadding = backgroundPadding * scale

    // 폰트 설정
    ctx.font = `${fontWeight} ${scaledFontSize}px "Pretendard", "Noto Sans KR", sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'

    // 텍스트 크기 측정
    const lines = text.split('\n')
    const lineHeight = scaledFontSize * 1.3
    let maxWidth = 0
    
    lines.forEach(line => {
      const metrics = ctx.measureText(line)
      maxWidth = Math.max(maxWidth, metrics.width)
    })

    // 위치 계산
    const x = canvasWidth / 2
    const y = canvasHeight - (canvasHeight - scaledYPosition)
    
    // 배경 그리기
    if (backgroundEnabled) {
      ctx.save()
      
      // 배경색 + 투명도
      const alpha = Math.min(Math.max(backgroundOpacity, 0), 1)
      ctx.fillStyle = backgroundColor
      ctx.globalAlpha = alpha
      
      // 멀티라인을 위한 전체 배경 박스
      const totalHeight = lines.length * lineHeight
      ctx.fillRect(
        x - maxWidth / 2 - scaledPadding,
        y - totalHeight,
        maxWidth + scaledPadding * 2,
        totalHeight + scaledPadding
      )
      
      ctx.restore()
    }

    // 텍스트 그리기 (멀티라인 지원)
    ctx.save()
    
    // 외각선 효과 (stroke)
    ctx.strokeStyle = '#000000'  // 검은색 외각선
    ctx.lineWidth = 4 * scale     // 외각선 두께
    ctx.lineJoin = 'round'        // 모서리 둥글게
    ctx.miterLimit = 2
    
    // 그림자 효과 (강화)
    ctx.shadowColor = 'rgba(0, 0, 0, 0.9)'
    ctx.shadowBlur = 6 * scale
    ctx.shadowOffsetX = 3 * scale
    ctx.shadowOffsetY = 3 * scale
    
    // 먼저 외각선 그리기
    lines.forEach((line, index) => {
      const lineY = y - (lines.length - 1 - index) * lineHeight
      ctx.strokeText(line, x, lineY)
    })
    
    // 그 다음 내부 텍스트 채우기
    ctx.fillStyle = fontColor
    lines.forEach((line, index) => {
      const lineY = y - (lines.length - 1 - index) * lineHeight
      ctx.fillText(line, x, lineY)
    })
    
    ctx.restore()
  }

  // 이미지 렌더링 (최적화: 이미지 캐싱, GIF 지원)
  // 이미지 초기 로드 (File 객체에서 이미지 로드)
  useEffect(() => {
    if (mp4Url || !mediaFile || !(mediaFile instanceof File) || imageLoaded) return
    
    const reader = new FileReader()
    reader.onload = (e) => {
      imageRef.current.onload = () => {
        setImageLoaded(true) // 이미지 로드 완료 표시
      }
      imageRef.current.src = e.target.result
    }
    reader.readAsDataURL(mediaFile)
  }, [mediaFile, mp4Url, imageLoaded]) // mediaFile 변경 시만 로드

  // renderImage 함수 제거 - useEffect로 대체됨

  // renderVideo 함수 제거 - 비디오 처리는 useEffect에서 직접 처리

  // 이미지와 텍스트 렌더링 통합 (모든 설정 실시간 반영)
  useEffect(() => {
    if (mp4Url || !imageLoaded || !mediaFile) return
    
    const canvas = canvasRef.current
    const img = imageRef.current
    if (!canvas || !img || !img.width) return
    
    // 디바운싱을 위한 타이머
    const timer = setTimeout(() => {
      const ctx = canvas.getContext('2d')
      
      // 캔버스 크기 설정
      canvas.width = width
      canvas.height = height
      
      // 이미지 비율 계산
      const imgAspect = img.width / img.height
      const canvasAspect = width / height
      const yOffset = videoSettings?.videoYOffset || 0
      const fillTo916 = videoSettings?.fillTo916 || false
      
      let drawWidth, drawHeight, drawX, drawY
      
      if (fillTo916) {
        if (imgAspect > canvasAspect) {
          drawHeight = height
          drawWidth = height * imgAspect
          drawX = (width - drawWidth) / 2
          drawY = yOffset
        } else {
          drawWidth = width
          drawHeight = width / imgAspect
          drawX = 0
          drawY = (height - drawHeight) / 2 + yOffset
        }
      } else {
        if (imgAspect > canvasAspect) {
          drawWidth = width
          drawHeight = width / imgAspect
          drawX = 0
          drawY = (height - drawHeight) / 2 + yOffset
        } else {
          drawHeight = height
          drawWidth = height * imgAspect
          drawX = (width - drawWidth) / 2
          drawY = yOffset
        }
      }
      
      // 배경 및 이미지 그리기
      ctx.fillStyle = '#000000'
      ctx.fillRect(0, 0, width, height)
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
      
      // 텍스트 오버레이 그리기
      drawSubtitle(ctx, width, height)
      drawTitle(ctx, width, height)
    }, 100) // 100ms 디바운싱
    
    return () => clearTimeout(timer)
  }, [
    // 미디어 관련
    mp4Url, mediaFile, imageLoaded,
    // 캔버스 크기
    width, height,
    // 비디오 설정
    videoSettings?.videoYOffset, videoSettings?.fillTo916,
    // 자막 관련
    text, settings,
    // 제목 관련
    titleText, titleSettings
  ]) // 모든 설정값 포함
  
  // 미디어 타입에 따라 렌디링 - 비디오 첫 프레임 즉시 표시
  useEffect(() => {
    if (mp4Url) {
      // MP4 URL이 있으면 비디오로 처리
      const video = videoRef.current
      const canvas = canvasRef.current
      
      if (!video || !canvas) return
      
      // console.log('🎬 비디오 설정 시작:', mp4Url)
      
      // 첫 프레임 그리기 함수 (설정값 실시간 반영)
      const drawFrame = () => {
        // console.log('drawFrame 호출 - video dimensions:', video.videoWidth, 'x', video.videoHeight)
        
        // 캔버스 크기를 9:16으로 고정
        canvas.width = 360
        canvas.height = 640
        
        // 캔버스 초기화
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = 'black'
        ctx.fillRect(0, 0, 360, 640)
        
        if (video.videoWidth && video.videoHeight) {
          const videoAspect = video.videoWidth / video.videoHeight
          const canvasAspect = canvas.width / canvas.height
          
          // 비디오 위치 설정 가져오기
          const yOffset = videoSettings?.videoYOffset || 0
          const fillTo916 = videoSettings?.fillTo916 || false
          
          let drawWidth, drawHeight, drawX, drawY
          
          if (fillTo916) {
            // 9:16 비율로 채우기 (cover 모드)
            if (videoAspect > canvasAspect) {
              drawHeight = 640
              drawWidth = 640 * videoAspect
              drawX = (360 - drawWidth) / 2
              drawY = yOffset
            } else {
              drawWidth = 360
              drawHeight = 360 / videoAspect
              drawX = 0
              drawY = (640 - drawHeight) / 2 + yOffset
            }
          } else {
            // 원본 비율 유지 (contain 모드)
            if (videoAspect > canvasAspect) {
              // 16:9 같은 넓은 비디오 - 가로를 캔버스 너비에 맞추고 세로는 중앙 정렬
              drawWidth = 360
              drawHeight = 360 / videoAspect
              drawX = 0
              drawY = (640 - drawHeight) / 2 + yOffset
            } else {
              // 9:16 같은 세로 비디오 - 세로를 캔버스 높이에 맞추고 가로는 중앙 정렬
              drawHeight = 640
              drawWidth = 640 * videoAspect
              drawX = (360 - drawWidth) / 2
              drawY = yOffset
            }
          }
          
          try {
            ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight)
            // console.log('✅ 비디오 프레임 그리기 성공')
            
            // 자막과 제목을 9:16 캔버스 전체 기준으로 그리기
            drawSubtitle(ctx, 360, 640)
            drawTitle(ctx, 360, 640)
            
            setVideoReady(true)
          } catch (e) {
            // console.log('❌ 그리기 실패:', e.message)
          }
        } else {
          // console.log('⚠️ 비디오 크기 정보 없음')
        }
      }
      
      // 이미 그려졌는지 추적하는 플래그
      let hasDrawn = false
      let drawTimer = null
      
      // 안전하게 그리기 함수 호출
      const safeDrawFrame = () => {
        if (!hasDrawn) {
          // 이전 타이머가 있으면 취소
          if (drawTimer) {
            clearTimeout(drawTimer)
          }
          // 짧은 지연 후 그리기 (중복 이벤트 방지)
          drawTimer = setTimeout(() => {
            drawFrame()
            hasDrawn = true
          }, 50)
        }
      }
      
      // 이벤트 리스너 먼저 등록
      video.onloadstart = () => {
        // console.log('🚀 loadstart')
        hasDrawn = false // 새로 로드 시작하면 리셋
      }
      video.onloadedmetadata = () => {
        // console.log('📊 loadedmetadata')
        safeDrawFrame()
      }
      video.onloadeddata = () => {
        // console.log('📦 loadeddata')
        safeDrawFrame()
      }
      video.oncanplay = () => {
        // console.log('✅ canplay')
        safeDrawFrame()
      }
      video.oncanplaythrough = () => {
        // console.log('💚 canplaythrough')
        safeDrawFrame()
      }
      video.onseeked = () => {
        // console.log('⏩ seeked - 시간 이동 완료')
        safeDrawFrame()
      }
      
      // 비디오 소스 설정
      // console.log('🔗 비디오 소스 설정:', mp4Url)
      video.src = mp4Url
      video.load() // 명시적으로 로드
      
      // 메타데이터 로드 후 시간 설정
      video.addEventListener('loadedmetadata', () => {
        video.currentTime = 1 // 1초 지점으로 이동하여 썸네일 표시
      }, { once: true })
      
      // 200ms 후 강제 그리기 (보험용)
      setTimeout(() => {
        if (!hasDrawn && video.readyState >= 2) {
          // console.log('⏰ 타이머로 강제 그리기')
          drawFrame()
          hasDrawn = true
        }
      }, 200)
      
      // cleanup
      return () => {
        video.onloadstart = null
        video.onloadedmetadata = null
        video.onloadeddata = null
        video.oncanplay = null
        video.oncanplaythrough = null
        video.onseeked = null
        if (drawTimer) {
          clearTimeout(drawTimer)
        }
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current)
        }
      }
    } else if (mediaFile) {
      // 파일이 있으면 이미지로 처리
      setImageLoaded(false)
      // renderImage 함수 호출 제거 - 더 이상 필요 없음
    }
  }, [
    // 미디어 관련
    mp4Url, mediaFile,
    // 비디오 설정
    videoSettings?.videoYOffset, videoSettings?.fillTo916,
    // 자막 관련
    text, settings,
    // 제목 관련
    titleText, titleSettings
  ]) // 모든 설정값 포함하여 실시간 반영

  // 비디오 클릭 시 재생/일시정지
  const handleCanvasClick = () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (video && mp4Url && canvas) {
      if (video.paused) {
        video.play()
        
        // 재생 중 애니메이션
        const animate = () => {
          // 캔버스 크기 고정
          canvas.width = 360
          canvas.height = 640
          
          const ctx = canvas.getContext('2d')
          ctx.fillStyle = 'black'
          ctx.fillRect(0, 0, 360, 640)
          
          if (video.videoWidth && video.videoHeight) {
            const videoAspect = video.videoWidth / video.videoHeight
            const canvasAspect = 360 / 640
            
            // 비디오 위치 설정 가져오기
            const yOffset = videoSettings?.videoYOffset || 0
            const fillTo916 = videoSettings?.fillTo916 || false
            
            let drawWidth, drawHeight, drawX, drawY
            
            if (fillTo916) {
              // 9:16 비율로 채우기 (cover 모드)
              if (videoAspect > canvasAspect) {
                drawHeight = 640
                drawWidth = 640 * videoAspect
                drawX = (360 - drawWidth) / 2
                drawY = yOffset
              } else {
                drawWidth = 360
                drawHeight = 360 / videoAspect
                drawX = 0
                drawY = (640 - drawHeight) / 2 + yOffset
              }
            } else {
              // 원본 비율 유지 (contain 모드)
              if (videoAspect > canvasAspect) {
                // 16:9 같은 넓은 비디오
                drawWidth = 360
                drawHeight = 360 / videoAspect
                drawX = 0
                drawY = (640 - drawHeight) / 2 + yOffset
              } else {
                // 9:16 같은 세로 비디오
                drawHeight = 640
                drawWidth = 640 * videoAspect
                drawX = (360 - drawWidth) / 2
                drawY = yOffset
              }
            }
            
            ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight)
            
            // 자막과 제목을 9:16 캔버스 전체 기준으로 그리기
            drawSubtitle(ctx, 360, 640)
            drawTitle(ctx, 360, 640)
          }
          
          if (!video.paused && !video.ended) {
            animationRef.current = requestAnimationFrame(animate)
          }
        }
        animate()
      } else {
        video.pause()
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current)
        }
      }
    }
  }
  

  return (
    <div className="canvas-preview-container" style={{ 
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%'
    }}>
      <canvas 
        ref={canvasRef}
        onClick={handleCanvasClick}
        style={{
          width: '100%',
          maxWidth: `${width}px`,
          height: 'auto',
          cursor: mp4Url ? 'pointer' : 'default',
          borderRadius: '8px',
          backgroundColor: '#000',
          display: 'block',
          margin: '0 auto'
        }}
      />
      
      {/* 숨겨진 비디오 요소 (Canvas 소스용) */}
      {mp4Url && (
        <video 
          ref={videoRef}
          style={{ display: 'none' }}
          loop
          muted
          playsInline
        />
      )}
      
    </div>
  )
}

export default CanvasSubtitlePreview